import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  date,
  index,
  integer,
  jsonb,
  numeric,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";

import { baFinancialPeriods } from "./schema-business-analytics.js";
import { accounts, clientGroups, contacts } from "./schema-crm.js";

export const groupFinancialPosition = pgTable(
  "crm_x_group_financial_position",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id"),
    clientGroupId: uuid("client_group_id")
      .notNull()
      .references(() => clientGroups.id, { onDelete: "cascade" }),
    primaryFye: date("primary_fye"),
    baseCurrency: varchar("base_currency", { length: 3 }).notNull().default("AUD"),
    lastVerifiedPeriod: uuid("last_verified_period_id").references(() => baFinancialPeriods.id),
    baseVersion: integer("base_version").notNull().default(1),
    stressBps: integer("stress_bps").notNull().default(100),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    clientGroupUnique: uniqueIndex("idx_group_financial_position_client_group_unique").on(table.clientGroupId),
    audOnly: check("chk_group_financial_position_base_currency_aud", sql`${table.baseCurrency} = 'AUD'`),
  }),
);

export const groupEbitdaComposer = pgTable(
  "crm_x_group_ebitda_composer",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id"),
    groupFinancialPositionId: uuid("group_financial_position_id")
      .notNull()
      .references(() => groupFinancialPosition.id, { onDelete: "cascade" }),
    accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
    financialPeriodId: uuid("financial_period_id").references(() => baFinancialPeriods.id, { onDelete: "set null" }),
    periodStartDate: date("period_start_date"),
    periodEndDate: date("period_end_date"),
    monthsInPeriod: integer("months_in_period"),
    rowLabel: text("row_label").notNull(),
    ebitdaAmount: numeric("ebitda_amount", { precision: 15, scale: 2 }).notNull().default("0"),
    normalisationAdjustments: numeric("normalisation_adjustments", { precision: 15, scale: 2 }).notNull().default("0"),
    dealAdjustments: numeric("deal_adjustments", { precision: 15, scale: 2 }).notNull().default("0"),
    adjustedEbitda: numeric("adjusted_ebitda", { precision: 15, scale: 2 }).notNull().default("0"),
    isWashRow: boolean("is_wash_row").notNull().default(false),
    washReason: text("wash_reason"),
    sourceProvenance: text("source_provenance"),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    washReasonRequired: check(
      "chk_group_ebitda_composer_wash_reason_required",
      sql`NOT ${table.isWashRow} OR length(trim(coalesce(${table.washReason}, ''))) > 0`,
    ),
    positionIdx: index("idx_group_ebitda_composer_position").on(table.groupFinancialPositionId),
    periodIdx: index("idx_group_ebitda_composer_period").on(table.financialPeriodId),
  }),
);

export const groupIntercompanyMatrix = pgTable("crm_x_group_intercompany_matrix", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  groupFinancialPositionId: uuid("group_financial_position_id")
    .notNull()
    .references(() => groupFinancialPosition.id, { onDelete: "cascade" }),
  fromAccountId: uuid("from_account_id").references(() => accounts.id, { onDelete: "set null" }),
  toAccountId: uuid("to_account_id").references(() => accounts.id, { onDelete: "set null" }),
  flowType: text("flow_type").notNull(),
  description: text("description"),
  annualAmount: numeric("annual_amount", { precision: 15, scale: 2 }).notNull().default("0"),
  periodLabel: text("period_label"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const groupPrincipalLoans = pgTable("crm_x_group_principal_loans", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  groupFinancialPositionId: uuid("group_financial_position_id")
    .notNull()
    .references(() => groupFinancialPosition.id, { onDelete: "cascade" }),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "set null" }),
  lenderName: text("lender_name"),
  facilityLabel: text("facility_label"),
  balanceAmount: numeric("balance_amount", { precision: 15, scale: 2 }).notNull().default("0"),
  interestRate: numeric("interest_rate", { precision: 15, scale: 5 }),
  annualRepayment: numeric("annual_repayment", { precision: 15, scale: 2 }),
  rateStressable: boolean("rate_stressable").notNull().default(true),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const groupDebtSchedule = pgTable(
  "crm_x_group_debt_schedule",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id"),
    groupFinancialPositionId: uuid("group_financial_position_id")
      .notNull()
      .references(() => groupFinancialPosition.id, { onDelete: "cascade" }),
    accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
    contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "set null" }),
    lenderName: text("lender_name"),
    facilityLabel: text("facility_label").notNull(),
    limitAmount: numeric("limit_amount", { precision: 15, scale: 2 }),
    balanceAmount: numeric("balance_amount", { precision: 15, scale: 2 }),
    interestRate: numeric("interest_rate", { precision: 15, scale: 5 }),
    termMonths: integer("term_months"),
    ioMonths: integer("io_months").notNull().default(0),
    repaymentType: text("repayment_type").notNull().default("principal_and_interest"),
    annualDebtService: numeric("annual_debt_service", { precision: 15, scale: 2 }),
    rateStressable: boolean("rate_stressable").notNull().default(true),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    oneBorrowerType: check(
      "chk_group_debt_schedule_account_or_contact",
      sql`(${table.accountId} IS NULL) <> (${table.contactId} IS NULL)`,
    ),
    accountIdx: index("idx_group_debt_schedule_account").on(table.accountId).where(sql`${table.accountId} IS NOT NULL`),
    contactIdx: index("idx_group_debt_schedule_contact").on(table.contactId).where(sql`${table.contactId} IS NOT NULL`),
  }),
);

export const groupDscrSnapshot = pgTable("crm_x_group_dscr_snapshot", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  groupFinancialPositionId: uuid("group_financial_position_id")
    .notNull()
    .references(() => groupFinancialPosition.id, { onDelete: "cascade" }),
  calcVersion: text("calc_version").notNull(),
  inputDigest: text("input_digest").notNull(),
  canonicalInputs: jsonb("canonical_inputs").notNull(),
  canonicalOutputs: jsonb("canonical_outputs").notNull(),
  baseVersion: integer("base_version").notNull(),
  dscr: numeric("dscr", { precision: 10, scale: 4 }),
  icr: numeric("icr", { precision: 10, scale: 4 }),
  stressedDscr: numeric("stressed_dscr", { precision: 10, scale: 4 }),
  isStale: boolean("is_stale").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const insertGroupFinancialPositionSchema = createInsertSchema(groupFinancialPosition).omit({ id: true, createdAt: true, updatedAt: true });
export const selectGroupFinancialPositionSchema = createSelectSchema(groupFinancialPosition);
export const insertGroupEbitdaComposerSchema = createInsertSchema(groupEbitdaComposer).omit({ id: true, createdAt: true, updatedAt: true });
export const selectGroupEbitdaComposerSchema = createSelectSchema(groupEbitdaComposer);
export const insertGroupDebtScheduleSchema = createInsertSchema(groupDebtSchedule).omit({ id: true, createdAt: true, updatedAt: true });
export const selectGroupDebtScheduleSchema = createSelectSchema(groupDebtSchedule);
export const insertGroupDscrSnapshotSchema = createInsertSchema(groupDscrSnapshot).omit({ id: true, createdAt: true });
export const selectGroupDscrSnapshotSchema = createSelectSchema(groupDscrSnapshot);

export type GroupFinancialPosition = typeof groupFinancialPosition.$inferSelect;
export type InsertGroupFinancialPosition = typeof groupFinancialPosition.$inferInsert;
export type GroupEbitdaComposer = typeof groupEbitdaComposer.$inferSelect;
export type InsertGroupEbitdaComposer = typeof groupEbitdaComposer.$inferInsert;
export type GroupDebtSchedule = typeof groupDebtSchedule.$inferSelect;
export type InsertGroupDebtSchedule = typeof groupDebtSchedule.$inferInsert;
export type GroupDscrSnapshot = typeof groupDscrSnapshot.$inferSelect;
export type InsertGroupDscrSnapshot = typeof groupDscrSnapshot.$inferInsert;
